/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { OnboardingModule } from '@coursera-web/cds-labs';

import { FormValidationStatus, Grid } from '@coursera/cds-core';

import ModularOnboardingQuestionRenderer from 'bundles/onboarding-common/components/modular/ModularOnboardingQuestionRenderer';
import { useOnboardingOptions } from 'bundles/onboarding-common/contexts/OnboardingOptionsContext';
import { useOnboardingProgress } from 'bundles/onboarding-common/contexts/OnboardingProgressContext';
import { useOnboardingQuestionData } from 'bundles/onboarding-common/contexts/OnboardingQuestionDataContext';
import useIsAdvancer from 'bundles/onboarding-common/hooks/useIsAdvancer';
import useSubmitAnswer from 'bundles/onboarding-common/hooks/useSubmitAnswer';
import { getQuestionTitleAndSupportingText } from 'bundles/onboarding-common/utils/modularOnboarding';

import _t from 'i18n!nls/onboarding-common';

const styles = {
  validationErrorMessage: css`
    background-color: transparent !important;

    span {
      color: var(--cds-color-neutral-primary);
    }
  `,
};

type Props = {
  onOnboardingCompletionSuccess: () => void;
  onDismiss: () => void;
};

const GenericOnboardingModuleRenderer = ({ onOnboardingCompletionSuccess, onDismiss }: Props) => {
  const { hasAnsweredQuestion, validationErrorMessage, resetQuestionData, setValidationErrorMessage } =
    useOnboardingQuestionData();
  const { questionIds, questionId, currentStep, setQuestionId, totalSteps } = useOnboardingProgress();
  const isAdvancer = useIsAdvancer();
  const { skillsType } = useOnboardingOptions();

  const { submitAnswer } = useSubmitAnswer();

  const [isDismissed, setIsDismissed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!questionId || isDismissed) {
    return null;
  }

  const isLastStep = currentStep === totalSteps;
  const isFirstStep = currentStep === 1;

  const { title, supportingText } = getQuestionTitleAndSupportingText(questionId, {
    isAdvancer,
    isPopularSkills: skillsType === 'POPULAR_SKILLS',
  });

  const handleOnNextButtonClick = async () => {
    setIsSubmitting(true);
    try {
      await submitAnswer();
      setIsSubmitting(false);
    } catch {
      setValidationErrorMessage(_t('There was an error saving your answer, please try again.'));
      setIsSubmitting(false);
      return;
    }

    // TODO: Eventing

    if (isLastStep) {
      onOnboardingCompletionSuccess();
    } else {
      resetQuestionData();
      setQuestionId(questionIds[currentStep]);
    }
  };

  const handleOnDismissButtonClick = () => {
    // TODO: Record dismiss action in BE
    // TODO: Eventing
    setIsDismissed(true);
    onDismiss();
  };

  const handleOnBackButtonClick = () => {
    // TODO: Eventing
    resetQuestionData();
    if (!isFirstStep) {
      setQuestionId(questionIds[currentStep - 2]);
    }
  };

  const nextAction = {
    label: isLastStep ? _t('Apply') : _t('Next'),
    onClick: handleOnNextButtonClick,
    disabled: !hasAnsweredQuestion || isSubmitting,
  };

  const dismissAction = {
    label: _t('Dismiss'),
    onClick: handleOnDismissButtonClick,
  };

  const backAction = !isFirstStep
    ? {
        label: _t('Back'),
        onClick: handleOnBackButtonClick,
      }
    : undefined;

  return (
    <OnboardingModule
      title={title}
      supportContent={supportingText}
      nextAction={nextAction}
      dismissAction={dismissAction}
      backAction={backAction}
    >
      <Grid container spacing={12}>
        <Grid item xs={12}>
          <ModularOnboardingQuestionRenderer />
        </Grid>
        {validationErrorMessage && (
          <Grid item xs={12}>
            <FormValidationStatus
              validationStatus="error"
              label={validationErrorMessage}
              css={styles.validationErrorMessage}
            />
          </Grid>
        )}
      </Grid>
    </OnboardingModule>
  );
};

export default GenericOnboardingModuleRenderer;
